import React, { ReactNode, useCallback, useContext, useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import DataCenter from '@/modules/data/DataCenter';
import { useLocalStorage } from './useLocalStorage';
import { LanguageState } from '@/type';

const Context = React.createContext<LanguageState>({
  language: '',
});

export const InternationalTranslationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const defaultLanguage = () => {
    let language = navigator.language.split(/[-_]/)[0];
    if (!DataCenter.SUPPORT_LANGUAGE[language]) {
      language = 'en';
    }
    return language;
  };

  const [language, setLanguage] = useLocalStorage<string>(
    DataCenter.LANGUAGE_KEY,
    defaultLanguage()
  );

  const switchLanguage = useCallback(
    (language: string) =>
      setLanguage?.(DataCenter.SUPPORT_LANGUAGE[language].language),
    [setLanguage]
  );

  const value = useMemo(
    () => ({
      language: language,
      messages: DataCenter.SUPPORT_LANGUAGE[language].translation,
      switchLanguage,
    }),
    [language, switchLanguage]
  );

  return (
    <Context.Provider value={value}>
      <IntlProvider
        key={value.language}
        locale={value.language}
        messages={value.messages}
        defaultLocale={value.language}
      >
        {children}
      </IntlProvider>
    </Context.Provider>
  );
};

export const useTranslation = (): LanguageState => {
  return useContext(Context);
};
